// Direct image download utility using html2canvas
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../config';

// Function to convert image to data URL to avoid CORS issues
const convertImageToDataURL = (img: HTMLImageElement): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }
    
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    
    try {
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    } catch (error) {
      reject(error);
    }
  });
};

// Function to preload and convert all images in an element
const preloadImages = async (element: HTMLElement): Promise<void> => {
  const images = element.querySelectorAll('img');
  const promises: Promise<void>[] = [];
  
  images.forEach((img) => {
    if (img.src && !img.src.startsWith('data:')) {
      const promise = new Promise<void>((resolve) => {
        if (img.complete) {
          convertImageToDataURL(img)
            .then(dataURL => {
              img.src = dataURL;
              resolve();
            })
            .catch(() => {
              // If conversion fails, leave original src
              resolve();
            });
        } else {
          img.onload = () => {
            convertImageToDataURL(img)
              .then(dataURL => {
                img.src = dataURL;
                resolve();
              })
              .catch(() => {
                resolve();
              });
          };
          img.onerror = () => resolve();
        }
      });
      promises.push(promise);
    }
  });
  
  await Promise.all(promises);
};

export const downloadElementAsImage = async (elementId: string, filename: string = 'image.png') => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error(`Element with id '${elementId}' not found`);
    }

    // Use html2canvas to capture the element
    const canvas = await html2canvas(element, {
      backgroundColor: null,
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: element.offsetWidth,
      height: element.offsetHeight,
      foreignObjectRendering: true,
      imageTimeout: 15000,
      removeContainer: true
    });

    // Convert canvas to blob and download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        throw new Error('Failed to create image blob');
      }
    }, 'image/png', 1.0);

  } catch (error) {
    console.error('Failed to download image:', error);
    throw error;
  }
};

export const downloadBadgeFromPreview = async (previewUrl: string, filename: string = 'badge.png') => {
  try {
    // Create a temporary iframe to load the badge
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '1200px';
    iframe.style.height = '800px';
    iframe.style.border = 'none';
    iframe.src = previewUrl;
    
    document.body.appendChild(iframe);
    
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        document.body.removeChild(iframe);
        reject(new Error('Timeout loading badge'));
      }, 10000);

      iframe.onload = () => {
        setTimeout(async () => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) {
              throw new Error('Cannot access iframe content - CORS issue');
            }
            
            // Find the badge container in the iframe
            const badgeContainer = iframeDoc.getElementById('badge-container') || 
                                 iframeDoc.getElementById('banner-container');
            
            if (!badgeContainer) {
              throw new Error('Badge container not found in iframe');
            }

            // Wait for all images to load completely
            const images = badgeContainer.querySelectorAll('img');
            const imagePromises: Promise<void>[] = [];
            
            images.forEach((img) => {
              if (!img.complete) {
                const promise = new Promise<void>((resolve) => {
                  img.onload = () => resolve();
                  img.onerror = () => resolve();
                });
                imagePromises.push(promise);
              }
            });
            
            await Promise.all(imagePromises);
            
            // Wait a bit more for rendering to complete
            await new Promise(resolve => setTimeout(resolve, 500));

            // Inject Inter font for consistent metrics
            const fontLink = iframeDoc.createElement('link');
            fontLink.rel = 'stylesheet';
            fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
            iframeDoc.head.appendChild(fontLink);

            // Inline images as data URLs with backend proxy fallbacks
            const backendHost = API_BASE_URL;
            const imgs = badgeContainer.querySelectorAll('img');
            const toDataUrl = (url: string) => fetch(url, { mode: 'cors', cache: 'no-store', credentials: 'omit' })
              .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
              .then(blob => new Promise<string>((res, rej) => { const fr = new FileReader(); fr.onloadend = () => res(fr.result as string); fr.onerror = rej; fr.readAsDataURL(blob); }));

            const imageTasks: Promise<void>[] = [];
            imgs.forEach(img => {
              const src = img.getAttribute('src') || '';
              if (!src || src.startsWith('data:')) return;
              let target = src;
              const tries: string[] = [];
              if (target.startsWith(backendHost) && !target.includes('/public/image-proxy')) {
                tries.push(backendHost + '/public/image-proxy?url=' + encodeURIComponent(target));
                if (target.includes('/static/')) {
                  const sp = target.split('/static/')[1];
                  if (sp) tries.push(backendHost + '/public/image-proxy/' + sp);
                }
              }
              tries.push(target);
              const p = tries.reduce((chain, url) => chain.catch(() => toDataUrl(url)), Promise.reject<string>(new Error('start')))
                .then(dataUrl => { img.setAttribute('src', dataUrl); })
                .catch(() => {
                  // last resort: reload with crossOrigin and cache-bust
                  (img as any).crossOrigin = 'anonymous';
                  const sep = target.includes('?') ? '&' : '?';
                  img.setAttribute('src', target + sep + 'cb=' + Date.now());
                });
              imageTasks.push(p);
            });

            await Promise.all(imageTasks);

            // Wait for fonts to be ready in iframe
            if ((iframeDoc as any).fonts && (iframeDoc as any).fonts.ready) {
              try {
                await (iframeDoc as any).fonts.ready;
              } catch {}
            } else {
              await new Promise(r => setTimeout(r, 600));
            }

            // Inject html-to-image in the iframe and capture
            const htiScript = iframeDoc.createElement('script');
            htiScript.src = 'https://cdn.jsdelivr.net/npm/html-to-image@1.11.11/dist/html-to-image.min.js';
            iframeDoc.head.appendChild(htiScript);

            htiScript.onload = async () => {
              try {
                const rect = (badgeContainer as HTMLElement).getBoundingClientRect();
                const maxDim = 3200; // cap output for the longer edge
                const scaleW = maxDim / Math.max(rect.width, 1);
                const scaleH = maxDim / Math.max(rect.height, 1);
                const pixelRatio = Math.min(4, Math.max(2, Math.min(scaleW, scaleH)));
                // @ts-ignore
                const dataUrl = await iframe.contentWindow.htmlToImage.toPng(badgeContainer as HTMLElement, {
                  cacheBust: true,
                  backgroundColor: 'transparent',
                  pixelRatio,
                  quality: 1,
                  skipFonts: false,
                  useCors: true,
                  fetchRequestInit: { mode: 'cors', cache: 'no-store', credentials: 'omit' }
                });

                const a = document.createElement('a');
                a.href = dataUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                
                clearTimeout(timeout);
                document.body.removeChild(iframe);
                resolve();
              } catch (error) {
                clearTimeout(timeout);
                document.body.removeChild(iframe);
                reject(error);
              }
            };

            htiScript.onerror = () => {
              clearTimeout(timeout);
              document.body.removeChild(iframe);
              reject(new Error('Failed to load html-to-image in iframe'));
            };

          } catch (error) {
            clearTimeout(timeout);
            document.body.removeChild(iframe);
            reject(error);
          }
        }, 2000);
      };

      iframe.onerror = () => {
        clearTimeout(timeout);
        document.body.removeChild(iframe);
        reject(new Error('Failed to load iframe'));
      };
    });

  } catch (error) {
    console.error('Failed to download badge:', error);
    throw error;
  }
};
